import { useState } from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ReceiptText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatCurrency } from "@/lib/utils";

// Itens da compra lançada pela foto da nota fiscal (Ultra, 07/10/2026): a despesa guarda a nota em `invoice` e o
// Extrato mostra uma etiqueta "🧾 N itens" que abre a lista do que foi comprado.
export interface NotaDaCompraDados {
  fornecedor: string;
  cnpj?: string;
  numero?: string;
  serie?: string;
  data: string;
  total: number;
  desconto?: number;
  acrescimo?: number;
  forma_pagamento?: string;
  itens: Array<{ descricao: string; quantidade?: number; unidade?: string; valor_unitario?: number; valor_total: number }>;
}

const qtd = (n: number) => n.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
const cnpjFmt = (d: string) =>
  d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : d.length === 11 ? d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4") : d;

export function EtiquetaNota({ nota, descricao }: { nota: NotaDaCompraDados; descricao: string }) {
  const [aberta, setAberta] = useState(false);
  const n = nota.itens.length;
  return (
    <>
      <button
        type="button"
        onClick={() => setAberta(true)}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary hover:bg-primary/15"
        data-testid="nota-itens"
      >
        <ReceiptText className="w-3 h-3" />
        {n === 1 ? "1 item" : `${n} itens`}
      </button>
      <Dialog open={aberta} onOpenChange={setAberta}>
        <DialogContent className="sm:max-w-md max-h-[90svh] overflow-y-auto">
          <DialogHeader>
            {/* pr-8: o título não passa por baixo do X; "(NF 48213)" não quebra no meio */}
            <DialogTitle className="text-left pr-8">{descricao.replace("(NF ", "(NF\u00a0")}</DialogTitle>
            <DialogDescription className="text-left">
              {[
                nota.cnpj ? `CNPJ ${cnpjFmt(nota.cnpj)}` : null,
                format(parseISO(nota.data), "dd/MM/yyyy", { locale: ptBR }),
                nota.forma_pagamento ?? null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </DialogDescription>
          </DialogHeader>
          <ul className="divide-y divide-border/60 text-sm" data-testid="nota-itens-lista">
            {nota.itens.map((it, i) => (
              <li key={i} className="flex items-start justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium leading-snug">{it.descricao}</p>
                  {it.quantidade != null && (
                    <p className="text-xs text-muted-foreground">
                      {qtd(it.quantidade)}
                      {it.unidade ? ` ${it.unidade}` : ""}
                      {it.valor_unitario != null ? ` × ${formatCurrency(it.valor_unitario)}` : ""}
                    </p>
                  )}
                </div>
                <span className="shrink-0 font-semibold tabular-nums">{formatCurrency(it.valor_total)}</span>
              </li>
            ))}
          </ul>
          <div className="space-y-1 border-t pt-2 text-sm">
            {!!nota.desconto && (
              <div className="flex justify-between text-muted-foreground">
                <span>Desconto</span>
                <span className="tabular-nums">−{formatCurrency(nota.desconto)}</span>
              </div>
            )}
            {!!nota.acrescimo && (
              <div className="flex justify-between text-muted-foreground">
                <span>Acréscimo</span>
                <span className="tabular-nums">+{formatCurrency(nota.acrescimo)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold">
              <span>Total da nota</span>
              <span className="tabular-nums">{formatCurrency(nota.total)}</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Lançada pela foto da nota que você mandou no WhatsApp do suporte.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
